import { Box, FormControl } from '@mui/material';
import { useCallback } from 'react';
import FieldLabel from '../../common/FieldLabel';
import { Typography, colors } from '@sweep-io/sweep-design';
import { TeamsRecipientsChipArray } from '../TeamsRecipientsChipArray';
import { TeamsRecipientInfo, TeamsRecipientStruct, TeamsRecipientType } from '../teams/types';
import { useRunOnce } from '../../common/useRunOnce';
import { TeamsSelectDealroomType } from './TeamsSelectDealroomType';
import { TeamsMessageEditor } from '../slack/TeamsMessageEditor';
import { SlackAdditionalFieldsTable } from '../slack/SlackAdditionalFieldsTable';
import { selectTeamsIntegrationStatus } from '../../../reducers/integrationsReducer';
import { useSelector } from 'react-redux';
import { TeamsConnectionStatus } from '../../../reducers/integrationsReducer';
import { TeamsNotConnected } from './TeamsNotConnected';

interface TeamsAddMemberActionProps {
  readonly?: boolean;
  action: TeamsAddMemberAutomationAction;
  crmOrgId: string;
  onChange: (teamsAddMemberAutomationAction: TeamsAddMemberAutomationAction) => any;
  objectName: string;
}

export const TeamsAddMemberAction = ({
  readonly,
  action,
  crmOrgId,
  objectName,
  onChange,
}: TeamsAddMemberActionProps) => {
  const teamsStatus = useSelector(selectTeamsIntegrationStatus);
  readonly = readonly || teamsStatus !== TeamsConnectionStatus.Connected;

  useRunOnce(async () => {
    updateOnChange({
      fields: [...(action.actionParams?.fields || [])],
      channelRef: action.actionParams?.channelRef
        ? { ...action.actionParams?.channelRef }
        : {
            type: TeamsRecipientType.CHANNEL_STORED_IN_SWEEP,
          },
    });
  });

  const updateOnChange = useCallback(
    (updateProps: Partial<AutomationTeamsAddMemberActionParams>) => {
      onChange({
        ...action,
        actionParams: {
          ...action.actionParams,
          ...updateProps,
        },
      });
    },
    [action, onChange],
  );

  const _onMembersChange = useCallback(
    (newRecipients: TeamsRecipientInfo[]) => {
      const newItems = newRecipients.map((el: TeamsRecipientInfo) => {
        if (el.type === TeamsRecipientType.USER_STORED_IN_SALESFORCE_FIELD) {
          return {
            ...el,
            type: TeamsRecipientType.USER_STORED_IN_SALESFORCE_FIELD,
            value: {
              fieldIds: Array.isArray(el.id) ? el.id : [el.id],
            },
            label: el.label,
          };
        } else if (el.type === TeamsRecipientType.CHANNEL_STORED_IN_SALESFORCE_FIELD) {
          return {
            ...el,
            type: TeamsRecipientType.CHANNEL_STORED_IN_SALESFORCE_FIELD,
            value: {
              fieldIds: Array.isArray(el.id) ? el.id : [el.id],
            },
            label: el.label,
          };
        } else {
          return {
            ...el,
            value: el.id,
            label: el.label,
          };
        }
      });

      updateOnChange({ members: newItems as TeamsRecipientStruct[] });
    },
    [updateOnChange],
  );

  return (
    <>
      {teamsStatus !== TeamsConnectionStatus.Connected && (
        <Box sx={{ marginTop: '32px' }}>
          <TeamsNotConnected teamsStatus={teamsStatus} />
        </Box>
      )}

      {(teamsStatus === TeamsConnectionStatus.Connected || action?.actionParams?.members) && (
        <Box
          sx={{
            background: colors.grey[100],
            padding: '24px 24px 19px',
            borderRadius: '8px',
            margin: '16px 0',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <TeamsSelectDealroomType
            readonly={readonly}
            value={action?.actionParams?.channelRef}
            crmOrgId={crmOrgId}
            objectName={objectName}
            onChange={(
              val: SweepCreatedTeamsChannel | SalesforceCreatedTeamsChannel | undefined,
            ) => {
              updateOnChange({ channelRef: val });
            }}
          />

          <FormControl
            sx={{
              width: '100%',
              marginBottom: '20px',
              maxWidth: '1148px',
            }}
          >
            <Box
              sx={{
                marginBottom: '3px',
                '& div': {
                  color: colors.grey[800],
                  fontSize: '12px',
                  fontWeight: '400',
                },
              }}
            >
              <FieldLabel label={'Add members'} markLabelAsRequired={true} />
            </Box>

            <TeamsRecipientsChipArray
              recipients={action.actionParams?.members || []}
              onChange={_onMembersChange}
              readonly={!!readonly}
              customButtonText="Add Users / Teams"
              crmOrgId={crmOrgId}
              objectName={objectName}
              usersOnly={true}
            />
          </FormControl>

          <TeamsMessageEditor
            placeholder="Type message body..."
            isRequired={false}
            label="Message"
            readonly={readonly}
            messageStruct={action.actionParams?.message ?? { template: '', variables: [] }}
            crmOrgId={crmOrgId}
            onChange={(newMsg) => {
              updateOnChange({ message: newMsg });
            }}
            objectName={objectName}
          />

          <SlackAdditionalFieldsTable
            readonly={readonly}
            fields={action.actionParams?.fields}
            updateOnChange={(fields) => {
              updateOnChange({ fields });
            }}
            crmOrgId={crmOrgId}
            objectName={objectName}
          />

          <Typography variant="body">
            Note: this automation would work as expected only if the record has a dedicated deal
            room
          </Typography>
        </Box>
      )}
    </>
  );
};
